import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Stack,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  Anchor as AnchorIcon,
  PhotoCameraOutlined as PhotoIcon,
  BoltOutlined as SpeedIcon,
  FactCheckOutlined as FactCheckIcon,
  DevicesOutlined as DevicesIcon,
  WorkspacePremiumOutlined as PremiumIcon,
  TravelExploreOutlined as SearchIcon,
  WarningAmberOutlined as HazardIcon,
  ArrowOutward as ArrowOutwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import GlobalReach from '../components/GlobalReach';
import PlatformPromo from '../components/PlatformPromo';
import { trackEvent, trackOutbound, trackCta } from '../utils/analytics';
import { services as serviceData } from '../data/services';
import inspectionHero from '../assets/images/hero-inspection.jpg';
import commitmentImg from '../assets/images/CommitmentToExcellence.avif';
import whyChooseUsImg from '../assets/images/WhyChooseUs.avif';

const stats = [
  { value: '11+', label: 'Years of combined QA/QC expertise' },
  { value: 'Global', label: 'Inspection coverage worldwide' },
  { value: 'ISO', label: 'Certified audit practices' },
  { value: '24/7', label: 'Response for port-side surveys' },
];

const serviceCards = serviceData;

const whyPoints = [
  'Certified, field-experienced surveyors',
  'Worldwide coverage, with bases in India',
  'Clear, photo-supported reporting',
  'Fast turnaround on time-critical jobs',
  'Independent, conflict-free assessments',
];

const imdgLookups = [
  'UN number & proper shipping name',
  'Hazard class & division',
  'Packing group & special provisions',
  'Emergency Schedules (EmS)',
  'Stowage & segregation requirements',
  'Limited & excepted quantities',
];

const hazardClasses = [
  'Explosives',
  'Gases',
  'Flammable Liquids',
  'Flammable Solids',
  'Oxidizers',
  'Toxic',
  'Radioactive',
  'Corrosive',
  'Lithium Batteries',
];

const IMDG_URL = 'https://imdg.inspectiq.in/';

const techFeatures = [
  {
    icon: <PhotoIcon />,
    title: 'Photo-Documented Evidence',
    description:
      'Every inspection is backed by time-stamped photographic evidence, so findings are clear and verifiable.',
  },
  {
    icon: <DevicesIcon />,
    title: 'Digital Field Data',
    description:
      'Surveyors capture data on-site using standardised digital checklists, reducing errors and omissions.',
  },
  {
    icon: <FactCheckIcon />,
    title: 'Standardised Reporting',
    description:
      'Consistent report formats across every job type make results easy to read, compare and share.',
  },
  {
    icon: <SpeedIcon />,
    title: 'Fast Turnaround',
    description:
      'Streamlined workflows let us deliver reports quickly, even on time-critical port-side jobs.',
  },
];

const testimonials = [
  {
    name: 'Operations Lead',
    company: 'Bulk commodities exporter',
    content:
      'Their draft survey reports are consistent and arrive on time, which keeps our vessel turnarounds on schedule. Communication at the berth is straightforward.',
  },
  {
    name: 'Logistics Manager',
    company: 'Container freight forwarder',
    content:
      'Pre-stuffing inspections are thorough and the photographic documentation has helped us settle cargo disputes without back-and-forth.',
  },
  {
    name: 'Procurement Head',
    company: 'Chemical manufacturer',
    content:
      'We rely on InspectIQ for independent quality and quantity checks. The findings are detailed and the team is responsive when timelines are tight.',
  },
];

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ color: 'secondary.dark', display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
);

const Home = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Seo
        title="Independent Marine & Cargo Inspection Worldwide"
        description="InspectIQ provides independent marine, cargo, container and project inspection services at ports worldwide, with bases in India. Fast, transparent, certified surveyors."
        path="/"
      />
      <PlatformPromo />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'calc(100vh - 120px)', md: 'calc(100vh - 120px)' },
          mt: { xs: '-64px', md: '-70px' },
          pt: { xs: '64px', md: '70px' },
          display: 'flex',
          alignItems: 'center',
          color: 'white',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${inspectionHero})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(100deg, rgba(1,38,55,0.92) 0%, rgba(1,62,92,0.82) 45%, rgba(1,86,122,0.45) 100%)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: 760 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
              <AnchorIcon sx={{ fontSize: 20, color: 'secondary.light' }} />
              <Typography
                variant="overline"
                sx={{ color: 'secondary.light', letterSpacing: '0.16em' }}
              >
                Independent Inspection & Survey Agency
              </Typography>
            </Stack>
            <Typography variant="h1" sx={{ mb: 2.5 }}>
              Inspection you can build decisions on
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif',
                color: 'rgba(255,255,255,0.88)',
                maxWidth: 620,
              }}
            >
              Independent marine, cargo and container inspection at ports across India
              and around the world — fast, transparent, and backed by certified surveyors.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => {
                  trackCta('Request a Quote', 'home_hero');
                  navigate('/quote');
                }}
                sx={{ px: 4, py: 1.5, fontSize: '1.05rem' }}
              >
                Request a Quote
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/services')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.05rem',
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Explore Services
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Stats Band */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container sx={{ py: { xs: 4, md: 5 } }}>
            {stats.map((stat, i) => (
              <Grid
                item
                xs={6}
                md={3}
                key={i}
                sx={{
                  textAlign: 'center',
                  py: { xs: 2, md: 0 },
                  borderRight: { md: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    color: 'secondary.light',
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)', px: 2 }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Overview */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>What We Do</SectionLabel>
          <Typography variant="h2" gutterBottom>
            Inspection services across the supply chain
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 680, mx: 'auto' }}>
            From the berth to the warehouse, our surveyors cover the checks that keep
            cargo moving and protect the parties relying on it.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {serviceCards.map((service) => (
            <Grid item xs={12} sm={6} md={3} key={service.slug}>
              <Card
                onClick={() => {
                  trackEvent('select_service', { service: service.slug, location: 'home' });
                  navigate(`/services/${service.slug}`);
                }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 18px 40px rgba(15, 39, 51, 0.12)',
                    borderColor: 'transparent',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={service.image}
                  alt={service.title}
                  sx={{ height: 210, objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontSize: '1.15rem' }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {service.shortDescription}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 'auto', color: 'primary.main' }}>
                    <Typography variant="button">Learn more</Typography>
                    <ArrowIcon fontSize="small" />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={whyChooseUsImg}
                alt="InspectIQ surveyors on site"
                sx={{
                  width: '100%',
                  height: { xs: 280, md: 420 },
                  objectFit: 'cover',
                  borderRadius: 3,
                  boxShadow: '0 20px 50px rgba(15, 39, 51, 0.18)',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionLabel>Why InspectIQ</SectionLabel>
              <Typography variant="h2" gutterBottom>
                Independent assessments, dependable reporting
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                We work as a neutral third party, so the findings you receive reflect the
                true condition of the cargo and vessel — nothing else.
              </Typography>
              <Stack spacing={1.5}>
                {whyPoints.map((point, index) => (
                  <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                    <CheckIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {point}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Global Reach (interactive) */}
      <GlobalReach />

      {/* Tech-Enabled Reporting */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Tech-Enabled Inspections</SectionLabel>
          <Typography variant="h2" gutterBottom>
            Modern reporting, dependable evidence
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 680, mx: 'auto' }}>
            We pair experienced surveyors with standardised digital workflows, so the
            reports you receive are accurate, consistent and delivered without delay.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {techFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', p: 3.5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: 'rgba(1, 86, 122, 0.08)',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* IMDG Search Tool */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Card
            sx={{
              overflow: 'hidden',
              border: 'none',
              boxShadow: '0 20px 50px rgba(15, 39, 51, 0.12)',
            }}
          >
            <Grid container>
              {/* Left: copy */}
              <Grid item xs={12} md={7}>
                <Box sx={{ p: { xs: 4, md: 6 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <HazardIcon sx={{ color: 'secondary.dark' }} />
                    <Typography variant="overline" sx={{ color: 'secondary.dark' }}>
                      Free Tool
                    </Typography>
                  </Stack>
                  <Typography variant="h2" gutterBottom>
                    IMDG Dangerous Goods Search
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    A free online lookup tool for the International Maritime Dangerous
                    Goods (IMDG) Code. Search by UN number or proper shipping name to find
                    classification and handling data in seconds — built for surveyors,
                    freight forwarders, shipping agents and port authorities.
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    {imdgLookups.map((item, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <CheckIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                  <Button
                    variant="contained"
                    size="large"
                    component="a"
                    href={IMDG_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackOutbound(IMDG_URL, 'imdg_home')}
                    endIcon={<ArrowOutwardIcon />}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Open IMDG Search Tool
                  </Button>
                </Box>
              </Grid>

              {/* Right: hazard classes panel */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: '100%',
                    p: { xs: 4, md: 6 },
                    background: 'linear-gradient(150deg, #013e5c 0%, #01567a 100%)',
                    color: 'white',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <SearchIcon sx={{ color: 'secondary.light', fontSize: 30 }} />
                    <Typography variant="h5" sx={{ color: 'white' }}>
                      All hazard classes
                    </Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {hazardClasses.map((hazard, index) => (
                      <Box
                        key={index}
                        sx={{
                          px: 1.75,
                          py: 0.75,
                          borderRadius: 999,
                          border: '1px solid rgba(255,255,255,0.3)',
                          fontSize: '0.85rem',
                          color: 'rgba(255,255,255,0.92)',
                        }}
                      >
                        {hazard}
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'rgba(255,255,255,0.65)' }}>
                    Always verify results against the official IMDG Code.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Expertise / Leadership */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: 'secondary.light', display: 'block', mb: 1 }}>
                Our Expertise
              </Typography>
              <Typography variant="h2" gutterBottom sx={{ color: 'white' }}>
                Led by deep QA/QC and commodity experience
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
                InspectIQ is led by professionals with over a decade of hands-on
                experience in quality assurance and quality control across the chemical
                and packaging sectors — including third-party inspection, auditing and
                supplier quality management. That expertise shapes how every inspection is
                scoped, carried out and reported.
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <PremiumIcon sx={{ color: 'secondary.light' }} />
                <Typography variant="body1" sx={{ color: 'white' }}>
                  Certified ISO Auditor on the leadership team
                </Typography>
              </Stack>
              <Button
                variant="contained"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/about')}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                Meet the Team
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <Stack spacing={2.5}>
                  {[
                    'Third-party inspection & audit',
                    'Chemical industry QA/QC',
                    'Packaging quality control',
                    'Supplier & vendor evaluation',
                    'Compliance & ISO standards',
                  ].map((item, index) => (
                    <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                      <CheckIcon sx={{ color: 'secondary.light' }} />
                      <Typography variant="body1" sx={{ color: 'white' }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Client Feedback</SectionLabel>
          <Typography variant="h2" gutterBottom>
            Trusted by exporters, forwarders and manufacturers
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', p: 3.5, bgcolor: '#f5f8fa' }}>
                <Typography
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: '3rem',
                    lineHeight: 1,
                    color: 'secondary.main',
                    mb: 1,
                  }}
                >
                  &ldquo;
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                  {testimonial.content}
                </Typography>
                <Box sx={{ borderTop: '1px solid rgba(15,39,51,0.1)', pt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {testimonial.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonial.company}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Vision and Innovation */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Looking Ahead</SectionLabel>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: 999, bgcolor: 'rgba(201,162,75,0.15)', mb: 2 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.dark' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'secondary.dark', letterSpacing: '0.05em' }}>
                Targeting launch in 2026
              </Typography>
            </Box>
            <Typography variant="h2" gutterBottom>
              Expanding into laboratory testing
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 680, mx: 'auto' }}>
              We are building dedicated testing facilities to complement our field
              inspection work with accredited analysis.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 4 }}>
                <Typography variant="h4" gutterBottom color="primary.main">
                  Upcoming Laboratory Services
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Our planned laboratory will extend our capabilities with accredited
                  analysis across several commodity and product categories:
                </Typography>
                <Grid container spacing={1.5}>
                  {['Chemical analysis', 'Agro product analysis', 'Food product analysis', 'Water, mineral, coal & sugar testing'].map(
                    (service, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <CheckIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {service}
                          </Typography>
                        </Stack>
                      </Grid>
                    )
                  )}
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  minHeight: 280,
                  display: 'flex',
                  alignItems: 'flex-end',
                  backgroundImage: `url(${commitmentImg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(1,38,55,0.1) 0%, rgba(1,38,55,0.85) 100%)',
                  },
                }}
              >
                <Box sx={{ p: 4, position: 'relative', zIndex: 1, color: 'white' }}>
                  <Typography variant="h4" gutterBottom>
                    Built on Accuracy
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Every facility we add is held to the same standard as our field work:
                    accurate, traceable and independent results our clients can act on.
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              justifyContent: 'space-between',
              gap: 4,
            }}
          >
            <Box>
              <Typography variant="h3" gutterBottom sx={{ color: 'white' }}>
                Have a shipment or vessel to inspect?
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
                Tell us what you need and we'll respond with a clear scope and quote.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                whiteSpace: 'nowrap',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Get in Touch
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
